import type { Feature, MapBrowserEvent } from 'ol';
import type { Coordinate } from 'ol/coordinate';
import { getWorldsAway } from 'ol/coordinate';
import { getWidth } from 'ol/extent';
import type { Geometry } from 'ol/geom';
import { defaults as OlInteractions } from 'ol/interaction';
import OlMap from 'ol/Map';
import type { Pixel } from 'ol/pixel';
import * as proj from 'ol/proj';
import View from 'ol/View';
import type { Ref } from 'vue';

import type { LatitudeLongitude, NoraExtent } from '@nora-monorepo/shared';
import { BBox, isNonEmptyString, isNonZero } from '@nora-monorepo/shared';
import { useWebViewStore } from '@nora-monorepo/ui-shared';

import { LONG_PRESS_MOVE_TOLERANCE_PIXEL } from '#nora-ui/map/composables/useMapContextMenu';
import { DrawFeatureSelectionManager } from '#nora-ui/map/map/DrawFeatureSelectionManager';
import { DrawToolManager } from '#nora-ui/map/map/DrawToolManager';
import { DrawToolUIManager } from '#nora-ui/map/map/DrawToolUIManager';
import { EventManager } from '#nora-ui/map/map/EventManager';
import { LayerManager } from '#nora-ui/map/map/LayerManager';
import { MapContextMenuManager } from '#nora-ui/map/map/MapContextMenuManager';
import { MapPersistenceManager } from '#nora-ui/map/map/MapPersistenceManager';
import { MapSetup } from '#nora-ui/map/map/MapSetup';
import { MapUtils } from '#nora-ui/map/map/MapUtils';
import { NoraMapRef } from '#nora-ui/map/map/NoraMapRef';
import { StateManager } from '#nora-ui/map/map/StateManager';
import type { MapConfig, UpdateMapLocationParameters } from '#nora-ui/map/types/map.types';
import { CompositeLayerId } from '#nora-ui/map/types/map.types';

/**
 * NoraMap is the main class responsible for managing the map instance,
 * including its layers, resources, events, and drawing tools.
 */
export class NoraMap {
  /**
   * The OpenLayers Map instance.
   */
  private olMap?: OlMap;

  /**
   * NoraMapRef - a reactive reference to the map configuration.
   */
  private noraMapRef: NoraMapRef;

  /**
   * StateManager - manages map resources such as markers, files, and draw layers.
   */
  private stateManager: StateManager;

  /**
   * LayerManager - manages map layers and their visibility.
   */
  private layerManager: LayerManager;

  /**
   * EventManager - handles map events and interactions.
   */
  private eventManager: EventManager;

  /**
   * DrawToolManager - manages draw tools and their interactions.
   */
  private drawToolManagerInstance: DrawToolManager;

  /**
   * DrawToolUIManager - manages the UI for draw tools.
   */
  private drawToolUIManagerInstance: DrawToolUIManager;

  /**
   * DrawFeatureSelectionManager - manages selecting persisted draw features on the map.
   */
  private drawFeatureSelectionManager: DrawFeatureSelectionManager;

  /**
   * MapPersistenceManager - manages offline persistence of map state.
   */
  private mapPersistenceManagerInstance: MapPersistenceManager;

  /**
   * The context menu manager for the map, handling right-click interactions and menu options.
   */
  private readonly contextMenuManager: MapContextMenuManager;

  // Throttles the hover-cursor hit-test to one run per frame (see setupPointerMoveInteractions).
  private cursorUpdateHandle: number | null = null;
  private pendingCursorPixel: Pixel | null = null;

  /**
   * Get the NoraMapRef instance.
   */
  get ref(): NoraMapRef {
    return this.noraMapRef;
  }

  /**
   * Get the StateManager instance.
   */
  get state(): StateManager {
    return this.stateManager;
  }

  /**
   * Get the LayerManager instance.
   */
  get layers(): LayerManager {
    return this.layerManager;
  }

  /**
   * Get the DrawToolManager instance.
   */
  get drawToolManager(): DrawToolManager {
    return this.drawToolManagerInstance;
  }

  /**
   * Get the MapContextMenuManager instance.
   */
  get contextMenu(): MapContextMenuManager {
    return this.contextMenuManager;
  }

  /**
   * Selects the draw feature
   */
  selectDrawFeature(feature: Feature<Geometry>): void {
    this.drawFeatureSelectionManager.selectFeature(feature);
  }

  /**
   * Get the DrawToolUIManager instance.
   */
  get drawToolUI(): DrawToolUIManager {
    return this.drawToolUIManagerInstance;
  }

  /**
   * Get the MapPersistenceManager instance.
   */
  get persistence(): MapPersistenceManager {
    return this.mapPersistenceManagerInstance;
  }

  /**
   * Get the current loading state of the map.
   * Returns true when reloadMapData() or an operation show/hide is in progress.
   */
  get isLoading(): boolean {
    return this.noraMapRef.isLoading;
  }

  /**
   * Get the current mouse coordinates on the map.
   * Updated on pointer move events.
   */
  get mouseCoordinates(): Ref<Coordinate | null> {
    return this.noraMapRef.mouseCoordinates;
  }

  /**
   * Setup a new NoraMap instance. Expects a mapConfig ref.
   * This will not initialize the map itself, for that call `setup` after constructing the NoraMap.
   * @param mapConfigRef
   */
  constructor(private readonly mapConfigRef: Ref<MapConfig>) {
    this.noraMapRef = new NoraMapRef(this.mapConfigRef);
    this.layerManager = new LayerManager(this);
    this.stateManager = new StateManager(this);
    this.eventManager = new EventManager(this);

    // Initialize DrawToolManager with the draw layer's vector source and layer
    const drawLayer = this.layerManager.draw.getLayer();
    this.drawToolManagerInstance = new DrawToolManager(
      this,
      drawLayer.getSource()!,
      drawLayer,
    );
    this.drawToolUIManagerInstance = new DrawToolUIManager(this);
    this.drawFeatureSelectionManager = new DrawFeatureSelectionManager(this);
    this.mapPersistenceManagerInstance = new MapPersistenceManager(this);
    this.contextMenuManager = new MapContextMenuManager(this);
  }

  /**
   * Setup the map within the provided HTML element.
   * @param element The HTML element to contain the map.
   */
  async setup(element: HTMLElement) {
    // Hydrate persisted state before setting up the map
    const wasHydrated = this.mapPersistenceManagerInstance.hydrate();

    this.setupMap(element);

    if (!wasHydrated) {
      await this.reloadMapData();
    }

    this.setupInteractions();
    await this.layerManager.setup();
    this.eventManager.setup();
    await this.drawFeatureSelectionManager.setup();

    if (wasHydrated) {
      // Hydrating puts the draw layers in state but renders nothing: only load() fills the
      // OpenLayers source, and we just skipped the reload that would have called it.
      await this.layerManager.draw.load();
    }

    // Setup persistence watchers after map is fully initialized
    this.mapPersistenceManagerInstance.setupWatchers();
  }

  /**
   * Reloads all map data for every shown operation (plus drafts and the current user).
   * This is the central method for a full reload of map state and layers.
   * Used when:
   * - Initial map setup
   * - Mobile WebView refresh when network is flaky
   */
  async reloadMapData(): Promise<void> {
    this.noraMapRef.isLoadingRef.value = true;

    try {
      await this.stateManager.load();
    }
    catch (error) {
      console.error('[NoraMap] Error loading map data:', error);
      // Don't rethrow - we want partial success and the individual managers already log their errors
    }
    finally {
      this.noraMapRef.isLoadingRef.value = false;
    }
  }

  /**
   * Shows an operation on the map and loads its data alongside any
   * operations already shown.
   * @param operationId The operation to show.
   */
  async showOperation(operationId: string): Promise<void> {
    this.noraMapRef.isLoadingRef.value = true;

    try {
      this.ref.addOperation(operationId);
      this.ref.setOperationVisibility(operationId, true);

      await this.stateManager.loadOperation(operationId);

      // The operation may have been hidden or removed while the load was in flight. Its
      // release already ran, so release again to drop whatever the load just committed.
      if (!this.ref.isOperationVisible(operationId)) {
        this.stateManager.releaseOperation(operationId);
      }
      else if (this.ref.activeOperationId === undefined) {
        // Showing an operation while there is no draw target makes it the target, so its
        // default draw layer (if any) immediately becomes the effective draw layer.
        this.setActiveOperation(operationId);
      }
    }
    catch (error) {
      console.error('[NoraMap] Error showing operation:', error);
    }
    finally {
      this.noraMapRef.isLoadingRef.value = false;
    }
  }

  /**
   * Hides an operation's data while keeping it on the map so it can be
   * shown again. Releases its loaded data; the other shown operations are untouched.
   * @param operationId The operation to hide.
   */
  hideOperation(operationId: string): void {
    this.ref.setOperationVisibility(operationId, false);
    this.releaseOperationData(operationId);
  }

  /**
   * Removes an operation from the map entirely and releases its data.
   * @param operationId The operation to remove.
   */
  removeOperation(operationId: string): void {
    this.ref.removeOperation(operationId);
    this.releaseOperationData(operationId);
  }

  /**
   * Sets the active draw-target operation. The active draw layer is left untouched so it
   * survives navigating back to the operations list; releasing an operation's data clears
   * it separately when it belonged to that operation.
   * @param operationId The operation to draw into, or undefined to clear
   */
  setActiveOperation(operationId: string | undefined): void {
    this.ref.setActiveOperation(operationId);
  }

  /**
   * Switches the map to exactly the given operation: removes every operation currently on
   * the map, then shows the requested one and makes it the draw target.
   * @param operationId The operation to switch to, or undefined to clear the map.
   */
  async switchToOperation(operationId: string | undefined): Promise<void> {
    for (const currentOperationId of this.ref.operationIds) {
      this.removeOperation(currentOperationId);
    }

    if (isNonEmptyString(operationId)) {
      await this.showOperation(operationId);

      // The operation may have been hidden or removed while the show was in flight;
      // activating it would resurrect an operation the user just dismissed.
      if (this.ref.isOperationVisible(operationId)) {
        this.setActiveOperation(operationId);
      }
    }
  }

  /**
   * Releases the loaded data of an operation and clears the draw target if it pointed at it.
   */
  private releaseOperationData(operationId: string): void {
    this.drawFeatureSelectionManager.deselectFeatureForOperation(operationId);
    this.stateManager.releaseOperation(operationId);

    if (this.ref.activeOperationId === operationId) {
      this.setActiveOperation(undefined);
    }
  }

  /**
   * Update the map location to the specified latitude, longitude, and optional zoom level.
   * @param parameters  The parameters containing latitude, longitude, and optional zoom level.
   */
  setCenter(parameters: UpdateMapLocationParameters): void {
    const {
      latitude,
      longitude,
      zoom,
    } = parameters;

    const normalizedLongitude = NoraMap.wrapLongitudeToValidRange(longitude);

    if (isNonZero(zoom)) {
      this.updateMapConfig({ zoom });
    }

    const olMap = this.getOlMapOrThrow();

    olMap.getView().animate({
      center: proj.fromLonLat([normalizedLongitude, latitude], this.ref.projection),
      zoom: this.ref.zoom,
      duration: 100, // Setting a duration is necessary to set the animating flag to true
    });
  }

  /**
   * Get the current zoom level of the map view.
   * @returns The current zoom level, or the reference zoom if the view has not reported a zoom yet.
   */
  getZoomLevel(): number {
    const olMap = this.getOlMapOrThrow();
    const mapZoomLevel = olMap.getView().getZoom();

    return mapZoomLevel ?? this.ref.zoom;
  }

  /**
   * Helper to get the map's ID. It is also available via `map.ref.id` but this is a convenience method.
   * @returns The map's ID.
   */
  getId(): string {
    return this.ref.id;
  }

  /**
   * Get the OpenLayers Map instance.
   * @returns The OpenLayers Map instance or undefined if not initialized.
   */
  getOlMap(): OlMap | undefined {
    return this.olMap;
  }

  /**
   * Get the OpenLayers Map instance or throw an error if not initialized.
   * @returns The OpenLayers Map instance.
   */
  getOlMapOrThrow(): OlMap {
    if (!this.olMap) {
      throw new Error('OpenLayers Map not initialized.');
    }

    return this.olMap;
  }

  /**
   * Get the current center of the map in latitude and longitude.
   * @returns The current center of the map.
   */
  getCenter(): LatitudeLongitude {
    const olMap = this.getOlMapOrThrow();
    const center = olMap.getView().getCenter();

    if (!center) {
      throw new Error('Map center is undefined.');
    }

    const [longitude, latitude] = proj.toLonLat(center, this.ref.projection);

    return { latitude, longitude };
  }

  /**
   * Get the attributions for the map layers.
   * @param showOnlyForVisibleLayers Whether to include attributions only for visible layers.
   * @returns An array of attribution strings.
   */
  getAttributions(showOnlyForVisibleLayers = true): string[] {
    const attributions: string[] = [];

    const layers = this.layerManager.getOlBaseLayers();
    const flattenedLayers = MapUtils.getLayersFlattened(layers);

    for (const layer of flattenedLayers) {
      const attribution = this.layerManager.getAttributionForLayer(layer, showOnlyForVisibleLayers);

      if (isNonEmptyString(attribution)) {
        attributions.push(attribution);
      }
    }

    return attributions;
  }

  /**
   * Get the bounding box of the current map view.
   * @returns The bounding box of the current map view.
   */
  getMapBbox(): BBox {
    const [minX, minY, maxX, maxY] = this.getMapExtent();
    const [minExtentX, minExtentY, maxExtentX, maxExtentY] = this.getMaxExtent();

    const clamped = [
      Math.max(minX, minExtentX),
      Math.max(minY, minExtentY),
      Math.min(maxX, maxExtentX),
      Math.min(maxY, maxExtentY),
    ];

    const [minLongitude, minLatitude, maxLongitude, maxLatitude]
      = proj.transformExtent(clamped, MapSetup.WEB_MERCATOR, MapSetup.WGS84);

    return new BBox(minLongitude, minLatitude, maxLongitude, maxLatitude);
  }

  /**
   * Get the current resolution of the map view.
   * @returns The current resolution.
   */
  getResolution(): number {
    return this.getOlMap()?.getView().getResolution() ?? 0;
  }

  /**
   * Get the current extent of the map view. Similar to getMapBbox but returns it in the map's projection and OpenLayers format.
   * @returns The current extent as [minX, minY, maxX, maxY].
   */
  getMapExtent(): NoraExtent {
    const olMap = this.getOlMapOrThrow();
    const extent = olMap.getView().calculateExtent(olMap.getSize());

    if (extent.some(value => Number.isNaN(value))) {
      throw new Error('Invalid extent values found.');
    }

    if (extent.length !== 4) {
      throw new Error('Invalid extent');
    }

    return extent as NoraExtent;
  }

  /**
   * Get the maximum extent of the map in the map's projection.
   * @returns The maximum extent as [minX, minY, maxX, maxY].
   */
  getMaxExtent(): NoraExtent {
    const maxExtent = proj.get(MapSetup.WEB_MERCATOR)?.getExtent();

    if (!maxExtent) {
      throw new Error('Web Mercator projection extent not found.');
    }

    if (maxExtent.length !== 4) {
      throw new Error('Invalid max extent');
    }

    return maxExtent as NoraExtent;
  }

  /**
   * Set the base map by its ID.
   * @param baseMapId The ID of the base map to set.
   */
  setBaseMap(baseMapId: string): void {
    if (this.ref.baseMap === baseMapId) {
      return;
    }

    this.updateBaseLayerVisibility(baseMapId);
    this.updateMapConfig({ baseMap: baseMapId });
  }

  /**
   * Toggle the visibility of an overlay map by its ID.
   * @param overlayMapId The ID of the overlay map to toggle.
   */
  toggleOverlayMapVisibility(overlayMapId: string): void {
    const currentVisibilityState = this.ref.getOverlayMapVisibility(overlayMapId);

    this.updateMapOverlayVisibility(overlayMapId, !currentVisibilityState);
    this.ref.setOverlayMapVisibility(overlayMapId, !currentVisibilityState);
  }

  /**
   * Toggle the visibility of an offline map by its ID.
   * @param offlineMapId The ID of the offline map to toggle.
   */
  toggleOfflineMapVisibility(offlineMapId: string): void {
    const currentVisibilityState = this.ref.getOfflineMapVisibility(offlineMapId);

    this.updateMapOverlayVisibility(offlineMapId, !currentVisibilityState);
    this.ref.setOfflineMapVisibility(offlineMapId, !currentVisibilityState);
  }

  /**
   * Set the zoom level of the map.
   * @param zoom The zoom level to set.
   */
  setZoom(zoom: number): void {
    this.updateMapConfig({ zoom });

    const olMap = this.getOlMapOrThrow();

    olMap.getView().setZoom(zoom);
  }

  /**
   * Set the rotation of the map.
   * @param rotation The rotation angle to set in radians.
   */
  setRotation(rotation: number): void {
    this.updateMapConfig({ rotation });

    const olMap = this.getOlMapOrThrow();

    olMap.getView().animate({
      rotation,
      duration: 100, // Setting a duration is necessary to set the animating flag to true
    });
  }

  /**
   * Fit the map view to the specified bounding box.
   * Transforms the BBox from geographic coordinates to the map's projection and delegates to fitToExtent.
   * @param bbox The bounding box to fit the view to.
   * @param padding The padding around the bounding box.
   * @param duration The duration of the fit animation.
   * @param maxZoom The maximum zoom level to use when fitting. If not provided, uses base map's maxZoom.
   */
  fitToBBox(bbox: BBox, padding: number | number[] = 50, duration = 250, maxZoom?: number): void {
    const transformedExtent = proj.transformExtent(
      [bbox.minLongitude, bbox.minLatitude, bbox.maxLongitude, bbox.maxLatitude],
      MapSetup.WGS84,
      this.ref.projection,
    ) as NoraExtent;

    this.fitToExtent(transformedExtent, padding, duration, maxZoom);
  }

  /**
   * Fit the map view to the specified extent (already in map projection).
   * @param extent The extent to fit the view to.
   * @param padding The padding around the extent.
   * @param duration The duration of the fit animation.
   * @param maxZoom The maximum zoom level to use when fitting. If not provided, uses base map's maxZoom.
   */
  fitToExtent(extent: number[], padding: number | number[] = 50, duration = 250, maxZoom?: number): void {
    let resolvedMaxZoom = maxZoom ?? 19;

    if (maxZoom === undefined && isNonEmptyString(this.ref.baseMap)) {
      const config = MapSetup.getInstance().getConfigForBaseMapId(
        this.ref.baseMap,
      );

      if (config) {
        resolvedMaxZoom = Math.min(config.maxZoom ?? resolvedMaxZoom, resolvedMaxZoom);
      }
    }

    const olMap = this.getOlMapOrThrow();

    const resolvedPadding = typeof padding === 'number'
      ? [padding, padding, padding, padding]
      : padding;

    olMap.getView().fit(extent, {
      duration,
      padding: resolvedPadding,
      maxZoom: resolvedMaxZoom,
    });
  }

  /**
   * Update the visibility of the base layers when changing the base map.
   * @param baseMapId The ID of the new base map to set.
   */
  private updateBaseLayerVisibility(
    baseMapId: string,
  ): void {
    const layerGroup = this.layerManager.getLayerGroupById(CompositeLayerId.BaseMaps);

    if (!layerGroup) {
      throw new Error('Base layer not added to map');
    }

    const oldBaseMapId = this.ref.baseMap;

    layerGroup.getLayers().forEach((layer) => {
      const layerId = layer.get('id') as string | undefined;

      if (layerId === baseMapId) {
        layer.setVisible(true);
      }
      else if (layerId === oldBaseMapId) {
        layer.setVisible(false);
      }
    });
  }

  /**
   * Update the visibility of an overlay layer.
   * @param overlayMapId The ID of the overlay map.
   * @param visible Whether the overlay map should be visible.
   */
  private updateMapOverlayVisibility(overlayMapId: string, visible: boolean): void {
    const layerGroup = this.layerManager.getLayerGroupById(CompositeLayerId.OverlayMaps);

    if (!layerGroup) {
      throw new Error('Overlay layer not added to map');
    }

    const overlayMapLayer = layerGroup.getLayers().getArray().find(
      layer => layer.get('id') === overlayMapId,
    );

    if (!overlayMapLayer) {
      throw new Error(`Overlay map with id ${overlayMapId} not found in layer group`);
    }

    overlayMapLayer.setVisible(visible);
  }

  /**
   * Setup map interactions such as panning and zooming.
   */
  private setupInteractions() {
    this.setupDefaultInteractions();
    this.setupCenterNormalizationOnMoveEnd();
    this.setupStateUpdateInteractions();
    this.setupPointerMoveInteractions();
    this.setupListenerForDeactivatingHeadingUpMode();
  }

  /**
   * Normalizes the map view center back into the canonical world after panning.
   * This prevents tools from creating geometries in shifted worlds after 360+ degree pans.
   */
  private setupCenterNormalizationOnMoveEnd(): void {
    const olMap = this.getOlMapOrThrow();

    olMap.on('moveend', () => {
      const view = olMap.getView();
      const center = view.getCenter();
      const projection = view.getProjection();

      if (!center || !projection.canWrapX()) {
        return;
      }

      const projectionExtent = projection.getExtent();

      const worldWidth = getWidth(projectionExtent);
      const worldsAway = getWorldsAway(center, projection, worldWidth);

      if (worldsAway === 0) {
        return;
      }

      view.setCenter([
        center[0] - worldsAway * worldWidth,
        center[1],
      ]);
    });
  }

  /**
   * Setup the default map interactions.
   */
  private setupDefaultInteractions() {
    const interactions = OlInteractions({
      keyboard: false,
    });

    const olMap = this.getOlMapOrThrow();

    olMap.getInteractions().clear();

    interactions.forEach((interaction) => {
      olMap.addInteraction(interaction);
    });
  }

  /**
   * Create interactions to update the map state on view changes.
   */
  private setupStateUpdateInteractions() {
    const olMap = this.getOlMapOrThrow();

    olMap.getView().on('change:center', () => {
      const center = olMap.getView().getCenter();

      if (center) {
        // Convert from map projection to lat/lng for storage
        const [longitude, latitude] = proj.toLonLat(center, this.ref.projection);

        if (latitude === undefined || longitude === undefined) {
          return;
        }
        this.updateMapConfig({
          center: {
            latitude,
            longitude,
          },
        });
      }
    });

    olMap.getView().on('change:resolution', () => {
      const zoom = olMap.getView().getZoom();

      if (zoom !== undefined) {
        this.updateMapConfig({ zoom });
      }
    });

    olMap.getView().on('change:rotation', () => {
      const rotation = olMap.getView().getRotation();

      if (rotation !== undefined) {
        this.updateMapConfig({ rotation });
      }
    });
  }

  /**
   * Setup pointer move interactions for cursor changes and coordinate tracking.
   * Changes cursor to pointer when hovering over features on clickable layers.
   */
  private setupPointerMoveInteractions() {
    const olMap = this.getOlMapOrThrow();

    const clickableLayers = this.layerManager.getClickableLayers();

    olMap.on('pointermove', (event: MapBrowserEvent) => {
      // Always track mouse coordinates (cheap).
      this.noraMapRef.mouseCoordinates.value = event.coordinate;

      // Skip cursor change during drag operations.
      if (event.dragging) {
        return;
      }

      // Throttle the expensive hit-test to one run per frame on the latest pointer position.
      this.pendingCursorPixel = event.pixel;

      if (this.cursorUpdateHandle !== null) {
        return;
      }

      this.cursorUpdateHandle = requestAnimationFrame(() => {
        this.cursorUpdateHandle = null;

        const pixel = this.pendingCursorPixel;

        if (!pixel) {
          return;
        }

        const hasClickableFeature = olMap.forEachFeatureAtPixel(
          pixel,
          () => true,
          { layerFilter: layer => clickableLayers.includes(layer) },
        );

        olMap.getTargetElement().style.cursor = hasClickableFeature ? 'pointer' : '';
      });
    });
  }

  /**
   * Sets up a listener for ending heading up mode.
   */
  private setupListenerForDeactivatingHeadingUpMode() {
    const olMap = this.getOlMapOrThrow();

    olMap.on('pointerdrag', () => {
      this.ref.setHeadingUpMode(false);
    });
  }

  /**
   * Setup the OpenLayers map instance.
   * @param element The HTML element to contain the map.
   */
  private setupMap(element: HTMLElement) {
    // Apply extra tolerance in webviews or when the main pointer is touch-like.
    const useTouchMoveTolerance = useWebViewStore().isWebView || window.matchMedia('(pointer: coarse)').matches;

    this.olMap = new OlMap({
      target: element,
      controls: [],
      moveTolerance: useTouchMoveTolerance
        ? LONG_PRESS_MOVE_TOLERANCE_PIXEL
        : undefined,
      view: new View({
        zoom: this.ref.zoom,
        projection: this.ref.projection,
        center: this.ref.center.toProjection(this.ref.projection),
      }),
    });

    this.olMap.getView().setRotation(this.ref.rotation);
  }

  /**
   * This updates the mapConfig ref with new values. The reason this is private is to prevent mapConfig updates without actually updating the map.
   * These updates should always go through the NoraMap methods to ensure the map config state is in sync with the internal memory config.
   * @param newConfig
   */
  private updateMapConfig(newConfig: Partial<MapConfig>): void {
    Object.assign(this.mapConfigRef.value, newConfig);
  }

  /**
   * Cleans up map resources when the map is destroyed.
   */
  private cleanup() {
    this.layerManager.track.cleanup();
    this.contextMenuManager.detach();
    this.mapPersistenceManagerInstance.cleanup();
    this.drawFeatureSelectionManager.cleanup();
    this.state.cleanup();
    this.eventManager.cleanup();
  }

  /**
   * Destroys the NoraMap instance and cleans up resources.
   */
  destroy() {
    this.cleanup();
  }

  /**
   * Wraps longitude into the valid range (-180 to 180).
   *
   * The same location can be written as values like 190 or -530.
   * This converts those values to one consistent longitude before use.
   */
  private static wrapLongitudeToValidRange(longitude: number): number {
    return ((longitude + 180) % 360 + 360) % 360 - 180;
  }
}
